import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, RefreshCw, X } from 'lucide-react';
import type {
  BackofficeVoucher,
  CreateBackofficeVoucherInput,
  UpdateBackofficeVoucherInput,
  VoucherRedemptionMode,
  VoucherStatus,
} from '../types';

const VOUCHER_CODE_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const VOUCHER_CODE_LENGTH = 10;

function padDatePart(value: number): string {
  return String(value).padStart(2, '0');
}

function generateVoucherCode(): string {
  let code = '';
  for (let index = 0; index < VOUCHER_CODE_LENGTH; index += 1) {
    const randomIndex = Math.floor(Math.random() * VOUCHER_CODE_ALPHABET.length);
    code += VOUCHER_CODE_ALPHABET[randomIndex];
  }
  return code;
}

function toDateTimeLocalValue(date: Date): string {
  const year = date.getFullYear();
  const month = padDatePart(date.getMonth() + 1);
  const day = padDatePart(date.getDate());
  const hours = padDatePart(date.getHours());
  const minutes = padDatePart(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toDateTimeLocalValueFromIso(value: string | null): string {
  return value ? toDateTimeLocalValue(new Date(value)) : '';
}

function isFutureDateTimeLocal(value: string): boolean {
  if (!value) return false;
  return new Date(value).getTime() > Date.now();
}

type CreateVoucherModalProps = {
  mode?: 'create';
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: CreateBackofficeVoucherInput) => Promise<void> | void;
};

type EditVoucherModalProps = {
  mode: 'edit';
  voucher: BackofficeVoucher;
  code: string | null;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: UpdateBackofficeVoucherInput) => Promise<void> | void;
};

export type VoucherModalProps = CreateVoucherModalProps | EditVoucherModalProps;

function resolveStatusOptions(voucher: BackofficeVoucher | null, startsAt: string) {
  const activeLabel = isFutureDateTimeLocal(startsAt) ? 'Scheduled' : 'Active';
  if (!voucher) {
    return [
      { value: 'draft' as const, label: 'Draft' },
      { value: 'active' as const, label: activeLabel },
    ];
  }

  switch (voucher.calculatedState) {
    case 'draft':
      return [
        { value: 'draft' as const, label: 'Draft' },
        { value: 'active' as const, label: activeLabel },
        { value: 'archived' as const, label: 'Archived' },
      ];
    case 'scheduled':
      return [
        { value: 'active' as const, label: activeLabel },
        { value: 'archived' as const, label: 'Archived' },
      ];
    case 'paused':
      return [
        { value: 'paused' as const, label: 'Paused' },
        { value: 'active' as const, label: 'Active' },
      ];
    default:
      return [
        { value: 'active' as const, label: activeLabel },
        { value: 'paused' as const, label: 'Paused' },
      ];
  }
}

export function CreateVoucherModal(props: VoucherModalProps) {
  const mode = props.mode ?? 'create';
  const { submitting, error, onClose } = props;
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const editVoucher = props.mode === 'edit' ? props.voucher : null;
  const [code, setCode] = useState(
    props.mode === 'edit' ? (props.code ?? props.voucher.codeDisplay) : ''
  );
  const [status, setStatus] = useState<VoucherStatus>(editVoucher?.status ?? 'active');
  const [startsAt, setStartsAt] = useState(
    editVoucher
      ? toDateTimeLocalValueFromIso(editVoucher.startsAt)
      : toDateTimeLocalValue(new Date())
  );
  const [redemptionMode, setRedemptionMode] = useState<VoucherRedemptionMode>(
    editVoucher?.redemptionMode ?? 'single_use'
  );
  const [maxRedemptions, setMaxRedemptions] = useState(String(editVoucher?.maxRedemptions ?? 1));
  const maxRedemptionsDisabled =
    redemptionMode === 'single_use' || redemptionMode === 'multi_use_uncapped';
  const maxRedemptionsDisplayValue = redemptionMode === 'multi_use_uncapped' ? '∞' : maxRedemptions;
  const statusOptions = resolveStatusOptions(editVoucher, startsAt);
  const title = mode === 'edit' ? 'Edit voucher' : 'Create voucher';
  const submitLabel = mode === 'edit' ? 'Save voucher' : 'Create voucher';
  const submittingLabel = mode === 'edit' ? 'Saving voucher' : 'Creating voucher';

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !submitting) {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, submitting]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;

    const form = new FormData(event.currentTarget);
    const maxRedemptionsValue = Number(maxRedemptions);
    const expiresAt = String(form.get('expiresAt') ?? '').trim();

    const sharedInput = {
      name: String(form.get('name') ?? ''),
      description: String(form.get('description') ?? '').trim() || null,
      status,
      interviewQuantity: Number(form.get('interviewQuantity')),
      redemptionMode,
      maxRedemptions:
        redemptionMode === 'single_use'
          ? 1
          : redemptionMode === 'multi_use_capped'
            ? maxRedemptionsValue
            : null,
      startsAt: new Date(startsAt).toISOString(),
      expiresAt: new Date(expiresAt).toISOString(),
    };

    if (props.mode === 'edit') {
      await props.onSubmit(sharedInput);
      return;
    }

    await props.onSubmit({
      code,
      ...sharedInput,
    });
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/45 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="voucher-modal-title"
        className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-6 py-5">
          <div>
            <h2
              id="voucher-modal-title"
              className="font-inter-tight text-xl font-semibold text-neutral-900"
            >
              {title}
            </h2>
            <p className="mt-1 text-body text-neutral-500">
              {mode === 'edit'
                ? 'Update voucher settings without changing the voucher code.'
                : 'Create a voucher code and define how many interviews it grants.'}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="rounded-md p-2 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900 disabled:cursor-not-allowed disabled:text-neutral-300"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <form
          className="max-h-[calc(92vh-96px)] overflow-y-auto px-6 py-5"
          onSubmit={(event) => {
            void handleSubmit(event);
          }}
        >
          {error ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-small font-semibold text-red-700">
              {error}
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-3">
            <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
              Code
              <span className="flex">
                <input
                  name="code"
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value.toUpperCase())}
                  disabled={mode === 'edit'}
                  className="h-10 min-w-0 flex-1 rounded-l-lg border border-r-0 border-neutral-200 bg-white px-3 font-mono text-body text-neutral-900 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500"
                />
                <button
                  type="button"
                  className="focus-ring inline-flex h-10 w-10 items-center justify-center rounded-r-lg border border-neutral-200 bg-white text-neutral-600 hover:border-backoffice-300 hover:text-backoffice-700 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-300"
                  aria-label="Regenerate code"
                  onClick={() => setCode(generateVoucherCode())}
                  disabled={mode === 'edit'}
                >
                  <RefreshCw className="size-4" aria-hidden="true" />
                </button>
              </span>
            </label>
            <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
              Customer facing name
              <input
                name="name"
                required
                defaultValue={editVoucher?.name ?? ''}
                className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
              />
            </label>
            <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
              Interviews (voucher value)
              <input
                name="interviewQuantity"
                required
                min={1}
                type="number"
                defaultValue={editVoucher?.interviewQuantity ?? ''}
                className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
              />
            </label>
            <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
              Status
              <select
                name="status"
                value={status}
                onChange={(event) => {
                  const nextStatus = event.target.value as VoucherStatus;
                  setStatus(nextStatus);
                  if (nextStatus === 'active' && !startsAt) {
                    setStartsAt(toDateTimeLocalValue(new Date()));
                  }
                }}
                className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
              Redemption
              <select
                name="redemptionMode"
                value={redemptionMode}
                onChange={(event) => {
                  const nextMode = event.target.value as VoucherRedemptionMode;
                  setRedemptionMode(nextMode);
                  if (nextMode === 'single_use') {
                    setMaxRedemptions('1');
                  } else if (nextMode === 'multi_use_capped' && maxRedemptions === '∞') {
                    setMaxRedemptions('1');
                  }
                }}
                className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
              >
                <option value="single_use">Single use</option>
                <option value="multi_use_capped">Capped multi-use</option>
                <option value="multi_use_uncapped">Uncapped multi-use</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
              Max redemptions
              <input
                name="maxRedemptions"
                value={maxRedemptionsDisplayValue}
                onChange={(event) => {
                  if (!maxRedemptionsDisabled) {
                    setMaxRedemptions(event.target.value);
                  }
                }}
                min={1}
                type={redemptionMode === 'multi_use_uncapped' ? 'text' : 'number'}
                disabled={maxRedemptionsDisabled}
                className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-500"
              />
            </label>
            <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
              Active from
              <input
                name="startsAt"
                value={startsAt}
                onChange={(event) => setStartsAt(event.target.value)}
                type="datetime-local"
                required
                className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
              />
            </label>
            <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
              Expires
              <input
                name="expiresAt"
                type="datetime-local"
                required
                defaultValue={toDateTimeLocalValueFromIso(editVoucher?.expiresAt ?? null)}
                className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
              />
            </label>
            <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700 md:col-span-2">
              Internal description
              <input
                name="description"
                defaultValue={editVoucher?.description ?? ''}
                className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
              />
            </label>
          </div>

          <div className="mt-6 flex justify-end border-t border-neutral-200 pt-5">
            <button
              type="submit"
              className="focus-ring inline-flex h-10 items-center gap-2 rounded-lg border border-backoffice-500 bg-backoffice-500 px-4 text-body font-semibold text-white transition hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:border-neutral-300 disabled:bg-neutral-300"
              disabled={submitting}
            >
              {submitting ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
              {submitting ? submittingLabel : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
